/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo, useState, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import { json, useLocation, useNavigate } from "react-router-dom";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// authentication context
export const AuthContext = createContext({
	isAuthenticated: false,
	login: () => {},
	register: () => {},
	logout: () => {},
	setAuth: () => {},
	hasAuth: () => [],
	setUser: () => {},
	getUser: () => {},
	getUserEmail: () => {},
});

const AuthContextProvider = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [auth, setAuthList] = useState([]); // Stato per le autorizzazioni (lista di stringhe)
	const [token, setToken] = useState(null); // Inizialmente null
	const [user, setUserData] = useState(null); // User data including email
	const navigate = useNavigate();
	const location = useLocation();

	/* gestione AUTH */
	const { sha3_256 } = require("js-sha3");

	function hashStringToSha3(input) {
		return sha3_256(input); // Calcola l'hash della stringa
	}

	function decodeJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split("")
					.map(function (c) {
						return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join("")
			);
			return JSON.parse(jsonPayload);
		} catch (error) {
			console.error("Error decoding JWT:", error);
			return null;
		}
	}

	useEffect(() => {
		const storedToken = localStorage.getItem("token");
		const storedAuth = localStorage.getItem("auth");
		const storedUser = localStorage.getItem("user");
		const publicRoutes = ["/auth/login", "/auth/forgot-password", "/auth/register", "/auth/reset-password"];

		if (storedAuth) {
			setAuthList(JSON.parse(storedAuth));
		}

		if (storedUser) {
			setUserData(JSON.parse(storedUser));
		} else if (storedToken) {
			// Try to decode user info from token if user data not stored
			const decoded = decodeJWT(storedToken);
			if (decoded) {
				setUserData(decoded);
				localStorage.setItem("user", JSON.stringify(decoded));
			}
		}

		setToken(storedToken);

		// Only redirect if not on a public route
		if (!storedToken && !publicRoutes.includes(location.pathname)) {
			navigate("/auth/login");
		} else if (storedToken) {
			setIsAuthenticated(true);
			// Keep current route after login
			navigate(location.pathname);
		}
	}, [navigate, location.pathname]);

	const login = (token, refresh) => {
		localStorage.setItem("token", token);
		if (refresh) {
			localStorage.setItem("refresh", refresh);
		}

		// Decode user info from token
		const decoded = decodeJWT(token);
		if (decoded) {
			setUserData(decoded);
			localStorage.setItem("user", JSON.stringify(decoded));
		}

		setToken(token);
		setIsAuthenticated(true);
		navigate("/selected-area");
	};

	const logout = () => {
		localStorage.removeItem("token");
		localStorage.removeItem("refresh");
		localStorage.removeItem("auth");
		localStorage.removeItem("user");
		setToken(null);
		setAuthList([]); // Resetta le autorizzazioni
		setUserData(null); // Reset user data
		setIsAuthenticated(false);
		navigate("/auth/login");
	};

	const setAuth = (newAuth) => {
		setAuthList(newAuth);
		localStorage.setItem("auth", JSON.stringify(newAuth));
	};

	const hasAuth = (authToCheck) => {
		return auth.includes(hashStringToSha3(authToCheck));
	};

	const setUser = (userData) => {
		setUserData(userData);
		localStorage.setItem("user", JSON.stringify(userData));
	};

	const getUser = () => {
		return user;
	};

	const getUserEmail = () => {
		if (user) {
			// Common JWT fields for email
			return user.email || user.sub || user.username || null;
		}
		return null;
	};

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				login,
				logout,
				setAuth,
				hasAuth,
				setUser,
				getUser,
				getUserEmail,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
	switch (action.type) {
		case "MINI_SIDENAV": {
			return { ...state, miniSidenav: action.value };
		}
		case "TRANSPARENT_SIDENAV": {
			return { ...state, transparentSidenav: action.value };
		}
		case "WHITE_SIDENAV": {
			return { ...state, whiteSidenav: action.value };
		}
		case "SIDENAV_COLOR": {
			return { ...state, sidenavColor: action.value };
		}
		case "TRANSPARENT_NAVBAR": {
			return { ...state, transparentNavbar: action.value };
		}
		case "FIXED_NAVBAR": {
			return { ...state, fixedNavbar: action.value };
		}
		case "OPEN_CONFIGURATOR": {
			return { ...state, openConfigurator: action.value };
		}
		case "DIRECTION": {
			return { ...state, direction: action.value };
		}
		case "LAYOUT": {
			return { ...state, layout: action.value };
		}
		case "DARKMODE": {
			return { ...state, darkMode: action.value };
		}
		default: {
			throw new Error(`Unhandled action type: ${action.type}`);
		}
	}
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
	const initialState = {
		miniSidenav: false,
		transparentSidenav: false,
		whiteSidenav: false,
		sidenavColor: "info",
		transparentNavbar: true,
		fixedNavbar: true,
		openConfigurator: false,
		direction: "ltr",
		layout: "dashboard",
		darkMode: false,
	};

	const [controller, dispatch] = useReducer(reducer, initialState);

	const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

	return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
	const context = useContext(MaterialUI);

	if (!context) {
		throw new Error("useMaterialUIController should be used inside the MaterialUIControllerProvider.");
	}

	return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export { AuthContextProvider, MaterialUIControllerProvider, useMaterialUIController, setMiniSidenav, setTransparentSidenav, setWhiteSidenav, setSidenavColor, setTransparentNavbar, setFixedNavbar, setOpenConfigurator, setDirection, setLayout, setDarkMode };
